import { type AdminPublic, type LoginInput } from '@uit/shared/schemas/auth';
import { AdminPublic as AdminPublicSchema } from '@uit/shared/schemas/auth';
import { type ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react';
import { apiMutation } from './api.js';

export interface AuthContextValue {
  admin: AdminPublic | null;
  loading: boolean;
  error: Error | null;
  login: (input: LoginInput) => Promise<void>;
  logout: () => Promise<void>;
  refetch: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [admin, setAdmin] = useState<AdminPublic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const res = await fetch('/api/auth/me', { credentials: 'include' });
      if (res.status === 401) {
        setAdmin(null);
        setError(null);
        return;
      }
      if (!res.ok) {
        throw new Error(`${res.status} ${res.statusText}`);
      }
      const json: unknown = await res.json();
      setAdmin(AdminPublicSchema.parse(json));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
      setAdmin(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (input: LoginInput): Promise<void> => {
    const result = await apiMutation<LoginInput, AdminPublic>('/api/auth/login', input, {
      schema: AdminPublicSchema,
    });
    setAdmin(result);
    setError(null);
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    await apiMutation('/api/auth/logout', {});
    setAdmin(null);
    setError(null);
  }, []);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const value: AuthContextValue = { admin, loading, error, login, logout, refetch };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}
